import React, { useEffect, useRef, useState } from "react";
import Navigation from "@/components/Navigation";
import ScrollProgress from "@/components/ScrollProgress";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import {
  Search, Loader2, XCircle, MessageSquare, Send,
  ShieldCheck, Clock, AlertCircle, CalendarDays, CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const GOLD = "#8A8A8A";
const GOLD_LIGHT = "#AAAAAA";

/* ── Starfield background ─────────────────── */
const DOTS = Array.from({ length: 60 }, (_, i) => ({
  id: i,
  x: `${(i * 17.3) % 100}%`,
  y: `${(i * 13.7) % 100}%`,
  r: 0.7 + (i % 3) * 0.6,
  op: 0.06 + (i % 5) * 0.04,
  dur: 4 + (i % 6),
  del: (i * 0.38) % 5,
}));

function StarField() {
  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
      {DOTS.map((d) => (
        <motion.div
          key={d.id}
          style={{
            position: "absolute",
            left: d.x,
            top: d.y,
            width: d.r * 2,
            height: d.r * 2,
            borderRadius: "50%",
            background: "#8A8A8A",
            opacity: d.op,
          }}
          animate={{ opacity: [d.op, d.op * 3.5, d.op] }}
          transition={{
            duration: d.dur,
            delay: d.del,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

/* ── Helpers ──────────────────────────────────── */
const RESCHEDULETIMES = [];
for (let h = 9; h <= 18; h++) {
  RESCHEDULETIMES.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 18) RESCHEDULETIMES.push(`${String(h).padStart(2, "0")}:30`);
}

function to12h(t) {
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
}
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function StatusBadge({ status }) {
  const styles = {
    pending:   { bg: "rgba(255,152,0,0.10)",  border: "rgba(255,152,0,0.40)",  color: "#FF9800", label: "Pending" },
    confirmed: { bg: "rgba(76,175,80,0.10)",  border: "rgba(76,175,80,0.40)",  color: "#4CAF50", label: "Confirmed" },
    cancelled: { bg: "rgba(244,67,54,0.10)",  border: "rgba(244,67,54,0.40)",  color: "#F44336", label: "Cancelled" },
    completed: { bg: "rgba(33,150,243,0.10)", border: "rgba(33,150,243,0.40)", color: "#2196F3", label: "Completed" },
    draft:     { bg: "rgba(138,138,138,0.10)",border: "rgba(138,138,138,0.40)",color: "#8A8A8A", label: "Draft" },
  };
  const s = styles[status] || styles.pending;
  return (
    <span
      style={{
        background: s.bg,
        borderColor: s.border,
        color: s.color,
        backdropFilter: "blur(12px)",
      }}
      className="inline-flex items-center gap-2.5 px-4 py-1.5 text-[11px] uppercase tracking-[0.25em] border font-mono"
      data-testid={`status-badge-${status}`}
    >
      <motion.span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: s.color }}
        animate={{ scale: [1, 1.3, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      />
      {s.label}
    </span>
  );
}

function ReschedulePicker({ onConfirm, onCancel, loading, submitRef }) {
  const now = new Date();
  const [selected, setSelected] = useState(null);
  const [chosenTime, setChosenTime] = useState("");
  const [reason, setReason] = useState("");
  const [closedDates, setClosedDates] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState({});
  const [fullyBlocked, setFullyBlocked] = useState([]);

  useEffect(() => {
    api.get("/closed-dates").then(({ data }) => setClosedDates(data.closed_dates || [])).catch(() => {});
    api.get("/availability").then(({ data }) => {
      setBlockedSlots(data.blocked_slots || {});
      setFullyBlocked(data.fully_blocked_dates || []);
    }).catch(() => {});
  }, []);

  const isOutsideSeason = (d) => {
    const y = d.getFullYear();
    return d < new Date(y, 5, 18) || d > new Date(y, 8, 6);
  };

  const disabled = (d) => {
    const dStr = fmtDate(d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (isOutsideSeason(d)) return true;
    if (closedDates.includes(dStr)) return true;
    if (fullyBlocked.includes(dStr)) return true;
    return false;
  };

  const blockedForDate = selected ? (blockedSlots[fmtDate(selected)] || []) : [];

  const submit = () => {
    if (!selected) return toast.error("Pick a date");
    if (!chosenTime) return toast.error("Pick a time");
    if (!reason.trim()) return toast.error("Please provide a reason");
    onConfirm(fmtDate(selected), chosenTime, reason);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 p-7 tile-spec"
    >
      <p className="overline mb-5 text-gold-bright">Request New Date</p>
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <div>
          <p className="text-xs text-muted mb-3">Select Date</p>
          <Calendar
            mode="single"
            selected={selected}
            onSelect={setSelected}
            disabled={disabled}
            defaultMonth={new Date(now.getFullYear(), now.getMonth())}
            className="bg-transparent text-foreground"
          />
        </div>
        <div>
          <p className="text-xs text-muted mb-3">Select Time</p>
          {!selected ? (
            <p className="text-faint text-sm">Pick a date first.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
              {RESCHEDULETIMES.map((t) => {
                const isBlocked = blockedForDate.includes(t);
                const active = chosenTime === t;
                return (
                  <motion.button
                    key={t}
                    onClick={() => {
                      if (!isBlocked) {
                        setChosenTime(t);
                        setTimeout(() => {
                          submitRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 100);
                      }
                    }}
                    disabled={isBlocked}
                    whileHover={!isBlocked ? { scale: 1.04 } : {}}
                    whileTap={!isBlocked ? { scale: 0.96 } : {}}
                    className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                      isBlocked
                        ? "text-foreground/20 border-stroke/30 cursor-not-allowed line-through"
                        : active
                        ? "bg-foreground text-background border-cream"
                        : "text-foreground/70 border-stroke hover:border-gold"
                    }`}
                  >
                    {to12h(t)}
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <textarea
        className="input-luxe mb-5 w-full"
        rows={3}
        placeholder="Why do you need to reschedule? (required)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        style={{ paddingLeft: 18 }}
      />
      <div className="flex gap-3 justify-end" ref={submitRef}>
        <button className="btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={loading}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Send Request
        </button>
      </div>
    </motion.div>
  );
}

/* Apple-style field row */
function Field({ label, children, className = "" }) {
  return (
    <div
      className="flex items-start gap-6 md:gap-10 py-4 border-b border-white/[0.06] last:border-0"
    >
      <p
        className="overline shrink-0 w-28 md:w-32"
        style={{ color: "rgba(245,245,247,0.45)", letterSpacing: "0.22em" }}
      >
        {label}
      </p>
      <p
        className={`text-foreground ${className}`}
        style={{ letterSpacing: "-0.01em", fontSize: "15.5px" }}
      >
        {children}
      </p>
    </div>
  );
}

/* ── MAIN COMPONENT ─────────────────────────────── */
export default function BookingStatus() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const initialId = params.get("id") || "";
  const initialToken = params.get("token") || "";
  const rescheduleAction = params.get("action");

  const [code, setCode] = useState(initialId);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [msgOpen, setMsgOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [clientToken, setClientToken] = useState(initialToken);
  const [sending, setSending] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleResponseOpen, setRescheduleResponseOpen] = useState(false);
  const [rescheduleAltOpen, setRescheduleAltOpen] = useState(false);

  const bookingDetailsRef = useRef(null);
  const rescheduleRef = useRef(null);
  const rescheduleResponseRef = useRef(null);
  const submitRef = useRef(null);

  const scrollTo = (ref, delay = 80) => {
    setTimeout(() => {
      ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, delay);
  };

  const lookup = async (val) => {
    const id = (val || code).trim();
    if (!id) return toast.error("Enter your booking reference");
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/lookup", { uuid: id });
      setBooking(data);
      setClientToken((prev) => data.client_token || prev);
      scrollTo(bookingDetailsRef, 200);
      if (rescheduleAction === "reschedule_response" && data.pending_reschedule?.requested_by === "admin") {
        setRescheduleResponseOpen(true);
      }
    } catch (e) {
      toast.error(errMsg(e, "Booking not found"));
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialId) lookup(initialId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialId]);

  const cancel = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/cancel`, { reason: cancelReason, client_token: clientToken });
      setBooking(data);
      setCancelOpen(false);
      setCancelReason("");
      toast.success("Booking cancelled");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't cancel"));
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!msg.trim()) return toast.error("Message is empty");
    setSending(true);
    try {
      await api.post(`/bookings/${booking.id}/contact`, { uuid: booking.id, message: msg, client_token: clientToken });
      toast.success("Message sent to Nisky Auto");
      setMsgOpen(false);
      setMsg("");
    } catch (e) {
      toast.error(errMsg(e, "Send failed"));
    } finally {
      setSending(false);
    }
  };

  const requestReschedule = async (newDate, newTime, reason) => {
    setSending(true);
    try {
      await api.post(`/bookings/${booking.id}/request-reschedule`, { new_date: newDate, new_time: newTime, reason, client_token: clientToken });
      toast.success("Reschedule request sent to Nisky Auto");
      setRescheduleOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't send request"));
    } finally {
      setSending(false);
    }
  };

  const respondToReschedule = async (action, newDate, newTime) => {
    setSending(true);
    try {
      const payload = { action, client_token: clientToken };
      if (action === "choose") { payload.new_date = newDate; payload.new_time = newTime; }
      const { data } = await api.post(`/bookings/${booking.id}/reschedule-response`, payload);
      if (action === "cancel") {
        setBooking((b) => ({ ...b, status: "cancelled" }));
        toast.success("Booking cancelled");
      } else if (action === "accept") {
        setBooking((b) => ({ ...b, appointment_date: data.new_date, appointment_time: data.new_time, pending_reschedule: null }));
        toast.success("Reschedule accepted");
      } else {
        toast.success("New date request sent to Nisky Auto");
      }
      setRescheduleResponseOpen(false);
      setRescheduleAltOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    } finally {
      setSending(false);
    }
  };

  const hasPendingAdminReschedule = booking?.pending_reschedule?.requested_by === "admin";

  return (
    <div
      className="min-h-screen text-foreground"
      style={{ background: "#0b0b0b" }}
      data-testid="status-page"
    >
      <ScrollProgress />
      <StarField />

      {/* Top + bottom radial glow */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          zIndex: 1,
          background:
            "radial-gradient(ellipse 80% 55% at 50% 0%, rgba(138,138,138,0.08) 0%, transparent 60%)",
        }}
      />

      <Navigation />

      <div
        className="relative pt-32 md:pt-40 pb-32 px-5 sm:px-8 max-w-4xl mx-auto"
        style={{ zIndex: 10 }}
      >
        {/* ── Hero heading ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20 md:mb-24"
        >
          <p
            className="overline mb-5"
            style={{ color: "rgba(170,170,170,0.85)" }}
          >
            Check Booking Status
          </p>
          <h1
            className="font-semibold tracking-[-0.045em] leading-[0.95]"
            style={{
              fontSize: "clamp(2.6rem, 8vw, 6rem)",
              color: "#f5f5f7",
              marginBottom: "48px",
            }}
          >
            Paste your{" "}
            <br className="hidden sm:block" />
            <em
              className="italic"
              style={{
                fontFamily: "'Instrument Serif', 'Fraunces', Georgia, serif",
                background: "var(--gold-gradient)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: "gold-shimmer 6s linear infinite",
              }}
            >
              booking reference.
            </em>
          </h1>

          {/* Search card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="glass-card p-6 md:p-8 max-w-2xl"
          >
            <p className="overline mb-4" style={{ color: "rgba(245,245,247,0.45)" }}>
              Reference
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                className="input-luxe flex-1 font-mono tracking-wider"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. A8F3B27D9E14"
                onKeyDown={(e) => e.key === "Enter" && lookup()}
                style={{ paddingLeft: 18 }}
                data-testid="status-input"
              />
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="btn-primary"
                onClick={() => lookup()}
                disabled={loading}
                data-testid="status-lookup-button"
              >
                {loading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Search size={14} />
                )}
                Look Up
                <ArrowRight size={13} style={{ opacity: 0.6 }} />
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* ── Loading skeleton ── */}
        <AnimatePresence mode="wait">
          {loading && !booking && (
            <motion.div
              key="skeleton"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
              className="glass-card-premium relative max-w-2xl"
              style={{ padding: "clamp(28px, 5vw, 48px)", overflow: "hidden" }}
              data-testid="booking-status-skeleton"
            >
              <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
                <div>
                  <Skeleton className="h-3 w-16 mb-3" />
                  <Skeleton className="h-10 w-48" />
                </div>
                <Skeleton className="h-7 w-28 rounded-full" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5 mb-8">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-8">
                <Skeleton className="h-10 w-32 rounded-full" />
                <Skeleton className="h-10 w-32 rounded-full" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Booking details ── */}
        <AnimatePresence mode="wait">
          {booking && (
            <motion.div
              ref={bookingDetailsRef}
              key={booking.id}
              initial={{ opacity: 0, y: 32, scale: 0.99 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.99 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="glass-card-premium relative"
              style={{ padding: "clamp(28px, 5vw, 48px)", overflow: "hidden" }}
              data-testid="booking-details"
            >
              {/* Gold top-right glow */}
              <div
                style={{
                  position: "absolute",
                  top: "-60px",
                  right: "-60px",
                  width: "240px",
                  height: "240px",
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(138,138,138,0.14) 0%, transparent 65%)",
                  pointerEvents: "none",
                }}
              />

              <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
                <div>
                  <p className="overline mb-3">Booking</p>
                  <p
                    className="font-mono text-2xl md:text-3xl text-foreground"
                    style={{ letterSpacing: "0.04em" }}
                  >
                    {booking.id}
                  </p>
                </div>
                <StatusBadge status={booking.status} />
              </div>

              <div className="mb-10">
                <Field label="Vehicle">
                  {booking.car?.year ? `${booking.car.year} ` : ""}
                  {booking.car?.make} {booking.car?.model}{" "}
                  <span style={{ color: "rgba(245,245,247,0.5)" }}>
                    · {booking.car?.body_type?.replace("-", " ")}
                  </span>
                </Field>
                <Field label="Condition" className="capitalize">
                  {booking.condition}
                </Field>
                <Field label="Service Address">{booking.address?.formatted}</Field>
                <Field label="Appointment">
                  {booking.appointment_date} at {to12h(booking.appointment_time)}
                </Field>
                <Field label="Estimated Total">
                  <span
                    className="font-mono"
                    style={{
                      color: GOLD_LIGHT,
                      fontSize: "1.05em",
                      fontWeight: 500,
                    }}
                  >
                    ${booking.price?.estimate}.00
                  </span>
                </Field>
                {booking.cancel_reason && (
                  <Field label="Cancel Reason">
                    <span style={{ color: "rgba(245,245,247,0.7)" }}>
                      {booking.cancel_reason}
                    </span>
                  </Field>
                )}
              </div>

              {/* Status messages */}
              {booking.status === "pending" && (
                <div className="flex items-start gap-3 p-5 tile-spec mb-7">
                  <Clock size={16} className="text-gold-bright mt-0.5" />
                  <p className="text-[14.5px]" style={{ color: "rgba(245,245,247,0.75)", letterSpacing: "-0.01em" }}>
                    We've received your booking. An admin will confirm it shortly. You'll be notified by email.
                  </p>
                </div>
              )}
              {booking.status === "confirmed" && (
                <div className="flex items-start gap-3 p-5 tile-spec mb-7">
                  <ShieldCheck size={16} className="text-gold-bright mt-0.5" />
                  <p className="text-[14.5px]" style={{ color: "rgba(245,245,247,0.75)", letterSpacing: "-0.01em" }}>
                    Your booking is confirmed. We'll see you on {booking.appointment_date}.
                  </p>
                </div>
              )}
              {booking.status === "completed" && (
                <div
                  className="flex items-start gap-3 p-5 mb-7"
                  style={{ background: "rgba(33,150,243,0.08)", border: "1px solid rgba(33,150,243,0.30)", borderRadius: 14 }}
                >
                  <CheckCircle2 size={16} className="mt-0.5" style={{ color: "#2196F3" }} />
                  <p className="text-[14.5px]" style={{ color: "rgba(245,245,247,0.75)", letterSpacing: "-0.01em" }}>
                    This job has been completed. Thank you for choosing Nisky Auto!
                  </p>
                </div>
              )}
              {booking.status === "cancelled" && (
                <div
                  className="flex items-start gap-3 p-5 mb-7"
                  style={{ background: "rgba(244,67,54,0.08)", border: "1px solid rgba(244,67,54,0.30)", borderRadius: 14 }}
                >
                  <AlertCircle size={16} className="mt-0.5" style={{ color: "#F44336" }} />
                  <p className="text-[14.5px]" style={{ color: "rgba(245,245,247,0.75)", letterSpacing: "-0.01em" }}>
                    This booking has been cancelled.
                  </p>
                </div>
              )}

              {/* Pending admin reschedule */}
              {hasPendingAdminReschedule && (
                <div
                  className="flex items-start gap-3 p-5 mb-7"
                  style={{ background: "rgba(138,138,138,0.08)", border: "1px solid rgba(138,138,138,0.30)", borderRadius: 14 }}
                >
                  <CalendarDays size={16} className="mt-0.5 text-gold-bright" />
                  <div>
                    <p className="text-[14.5px] text-foreground font-medium mb-1" style={{ letterSpacing: "-0.01em" }}>
                      Reschedule Requested
                    </p>
                    <p className="text-[14.5px]" style={{ color: "rgba(245,245,247,0.75)", letterSpacing: "-0.01em" }}>
                      Nisky Auto has requested to reschedule your appointment to{" "}
                      <strong className="text-foreground">{booking.pending_reschedule.new_date}</strong> at{" "}
                      <strong className="text-foreground">{to12h(booking.pending_reschedule.new_time)}</strong>.
                    </p>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {booking.status !== "cancelled" && booking.status !== "completed" && (
                <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                  <motion.button
                    whileHover={{ scale: 1.03, y: -1 }}
                    whileTap={{ scale: 0.97 }}
                    className="btn-secondary"
                    onClick={() => setMsgOpen(true)}
                    data-testid="status-message-button"
                  >
                    <MessageSquare size={14} /> Send a Message
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.03, y: -1 }}
                    whileTap={{ scale: 0.97 }}
                    className="btn-secondary"
                    onClick={() => { setRescheduleOpen(true); scrollTo(rescheduleRef, 120); }}
                    data-testid="status-reschedule-button"
                  >
                    <CalendarDays size={14} /> Request Reschedule
                  </motion.button>
                  {hasPendingAdminReschedule && (
                    <motion.button
                      whileHover={{ scale: 1.03, y: -1 }}
                      whileTap={{ scale: 0.97 }}
                      className="btn-primary"
                      onClick={() => { setRescheduleResponseOpen(true); scrollTo(rescheduleResponseRef, 120); }}
                      data-testid="status-respond-button"
                    >
                      <CalendarDays size={14} /> Respond to Reschedule
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.03, y: -1 }}
                    whileTap={{ scale: 0.97 }}
                    className="btn-secondary"
                    onClick={() => setCancelOpen(true)}
                    style={{ borderColor: "rgba(244,67,54,0.30)", color: "#F44336" }}
                    data-testid="status-cancel-button"
                  >
                    <XCircle size={14} /> Cancel Booking
                  </motion.button>
                </div>
              )}

              {/* Cancel form */}
              {cancelOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 p-7 border border-red-500/30 bg-red-500/5"
                  style={{ borderRadius: 14 }}
                >
                  <p className="overline mb-5" style={{ color: "#F44336" }}>Cancel Booking</p>
                  <textarea
                    className="input-luxe mb-5"
                    rows={3}
                    placeholder="Reason (optional)"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    style={{ paddingLeft: 18 }}
                    data-testid="cancel-reason-input"
                  />
                  <div className="flex gap-3 justify-end">
                    <button className="btn-secondary" onClick={() => setCancelOpen(false)}>Never mind</button>
                    <button
                      className="btn-primary"
                      style={{ background: "#F44336", borderColor: "#F44336", color: "#fff" }}
                      onClick={cancel}
                      disabled={sending}
                      data-testid="confirm-cancel-button"
                    >
                      {sending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                      Confirm Cancel
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Message form */}
              {msgOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 p-7 tile-spec"
                >
                  <p className="overline mb-5">Message Nisky Auto</p>
                  <textarea
                    className="input-luxe mb-5"
                    rows={4}
                    placeholder="What would you like us to know?"
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                    style={{ paddingLeft: 18 }}
                    data-testid="contact-message-input"
                  />
                  <div className="flex gap-3 justify-end">
                    <button className="btn-secondary" onClick={() => setMsgOpen(false)}>Cancel</button>
                    <button className="btn-primary" onClick={sendMessage} disabled={sending} data-testid="contact-send-button">
                      {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      Send
                    </button>
                  </div>
                </motion.div>
              )}

              {rescheduleOpen && (
                <div ref={rescheduleRef}>
                  <ReschedulePicker
                    onConfirm={requestReschedule}
                    onCancel={() => setRescheduleOpen(false)}
                    loading={sending}
                    submitRef={submitRef}
                  />
                </div>
              )}

              {rescheduleResponseOpen && hasPendingAdminReschedule && !rescheduleAltOpen && (
                <motion.div
                  ref={rescheduleResponseRef}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 p-7 tile-spec"
                >
                  <p className="overline mb-4">Respond to Reschedule Request</p>
                  <p className="text-[14.5px] mb-6" style={{ color: "rgba(245,245,247,0.75)", letterSpacing: "-0.01em" }}>
                    We'd like to move your appointment to{" "}
                    <strong className="text-foreground">{booking.pending_reschedule.new_date}</strong> at{" "}
                    <strong className="text-foreground">{to12h(booking.pending_reschedule.new_time)}</strong>.
                    What would you like to do?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button className="btn-primary" onClick={() => respondToReschedule("accept")} disabled={sending}>
                      <CheckCircle2 size={14} /> Accept New Time
                    </button>
                    <button className="btn-secondary" onClick={() => { setRescheduleAltOpen(true); scrollTo(rescheduleRef, 120); }}>
                      <CalendarDays size={14} /> Choose Different Date
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => respondToReschedule("cancel")}
                      disabled={sending}
                      style={{ borderColor: "rgba(244,67,54,0.30)", color: "#F44336" }}
                    >
                      <XCircle size={14} /> Cancel Booking
                    </button>
                  </div>
                </motion.div>
              )}

              {rescheduleAltOpen && (
                <div ref={rescheduleRef}>
                  <ReschedulePicker
                    onConfirm={(d, t, r) => respondToReschedule("choose", d, t)}
                    onCancel={() => setRescheduleAltOpen(false)}
                    loading={sending}
                    submitRef={submitRef}
                  />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <p
          className="text-center text-xs mt-20"
          style={{ color: "rgba(245,245,247,0.25)", letterSpacing: "0.02em" }}
        >
          <Link to="/privacy" className="hover:text-gold transition-colors mr-5" style={{ color: "rgba(245,245,247,0.35)" }}>
            Privacy Policy
          </Link>
          <Link to="/terms" className="hover:text-gold transition-colors" style={{ color: "rgba(245,245,247,0.35)" }}>
            Terms of Service
          </Link>
        </p>
      </div>
    </div>
  );
}
